import { useEffect, useMemo, useState } from "react";
import { uploadImage } from "@/services/upload";
import { Box, Card, CardMedia, IconButton } from "@mui/material";
import { Cancel } from "@mui/icons-material";

/**
 * Uploads a list of File objects, stores the remote URLs in `meta.urls`, and
 * renders local previews that can be removed individually. Preview object
 * URLs are revoked automatically to avoid memory leaks.
 */
export default function NewCarouselContainer({ files, meta, setMeta }) {
  const [previews, setPreviews] = useState([]);

  // Normalise to an array so we can depend on identity changes only.
  const fileArray = useMemo(() => (files ? Array.from(files) : []), [files]);

  /**
   * Build local preview URLs whenever the file list changes and clean them up
   * on unmount or when the list changes again.
   */
  useEffect(() => {
    const nextPreviews = fileArray.map((file) => ({
      url: URL.createObjectURL(file),
      file,
    }));

    setPreviews(nextPreviews);

    return () => {
      nextPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [fileArray]);

  /**
   * Upload all files whenever the list changes. The async work is cancelled if
   * the component unmounts before it finishes.
   */
  useEffect(() => {
    let cancelled = false;

    const upload = async () => {
      const urls = await Promise.all(fileArray.map(uploadImage));
      if (!cancelled) {
        setMeta((prev) => ({ ...prev, urls }));
      }
    };

    if (fileArray.length) upload();

    return () => {
      cancelled = true;
    };
  }, [fileArray, setMeta]);

  /**
   * Remove a single preview and its corresponding remote URL.
   */
  const handleRemove = (index) => {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setMeta((prev) => ({
      ...prev,
      urls: (prev?.urls || []).filter((_, i) => i !== index),
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: 2,
        flexWrap: "wrap",
        mt: 2,
      }}
    >
      {previews.map(({ url }, index) => (
        <Card
          key={`image-${index}`}
          sx={{ borderRadius: 2, flexShrink: 0, position: "relative" }}
        >
          <IconButton
            size="small"
            onClick={() => handleRemove(index)}
            sx={{ position: "absolute", top: 0, right: 0 }}
          >
            <Cancel color="info" fontSize="small" />
          </IconButton>
          <CardMedia
            component="img"
            src={url}
            sx={{ borderRadius: 1, height: 150, width: 150 }}
          />
        </Card>
      ))}
    </Box>
  );
}
